/**
 * Module-scope LRU cache for the Flex coding dropdown hook.
 *
 * Extracted from `useFlexCodingDropdown` so callers outside the hook (e.g.
 * the auth slice on company switch / sign out) can call
 * `invalidateFlexOptionsCache()` without importing the hook itself, which
 * would create a circular dependency through the Redux store.
 */

export interface FlexCodingCacheEntry {
  items: unknown[];
  nextToken: string | null;
  total: number | undefined;
  timestamp: number;
}

export const FLEX_CACHE_TIME_TO_LIVE_MS = 5 * 60 * 1000;
export const FLEX_CACHE_MAX_ENTRIES = 200;

/**
 * What a shared in-flight fetch resolves to.
 *
 * SEE-1392: this deliberately carries the failure instead of collapsing it to
 * `null`. The promise is created by whichever hook instance got there first,
 * but every instance that joins through the in-flight dedup branch awaits the
 * same promise — so if the outcome cannot distinguish "no matches" from
 * "the search was denied", the joining instances render "No options found"
 * on a failure while the originating one renders the error copy. On a
 * transactions table that is N-1 of N dropdowns, since every row's dropdown of
 * a given coding type mounts in the same tick with `term: ''` and therefore
 * shares one cache key.
 */
export interface FlexCodingFetchOutcome {
  entry: FlexCodingCacheEntry | null;
  failed: boolean;
  error: string | null;
}

export const flexOptionsCache = new Map<string, FlexCodingCacheEntry>();
export const flexInflightRequests = new Map<string, Promise<FlexCodingFetchOutcome>>();

export function makeFlexCacheKey(
  codingType: string,
  paidolId: string,
  glCodeId?: string,
  flexCodeId?: string,
  searchTerm?: string,
  cardGroupId?: string
): string {
  return `${codingType}|${paidolId}|${glCodeId ?? ''}|${flexCodeId ?? ''}|${searchTerm ?? ''}|${
    cardGroupId ?? ''
  }`;
}

// Map preserves insertion order, so delete+set on read moves an entry to "most
// recently used"; eviction drops the oldest (first-iterated) key when we overflow.
export function getFlexCached(key: string): FlexCodingCacheEntry | null {
  const entry = flexOptionsCache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.timestamp > FLEX_CACHE_TIME_TO_LIVE_MS) {
    flexOptionsCache.delete(key);
    return null;
  }
  flexOptionsCache.delete(key);
  flexOptionsCache.set(key, entry);
  return entry;
}

export function setFlexCached(key: string, entry: FlexCodingCacheEntry): void {
  if (flexOptionsCache.has(key)) flexOptionsCache.delete(key);
  flexOptionsCache.set(key, entry);
  if (flexOptionsCache.size > FLEX_CACHE_MAX_ENTRIES) {
    const oldestKey = flexOptionsCache.keys().next().value;
    if (oldestKey !== undefined) flexOptionsCache.delete(oldestKey);
  }
}

/** Clear all or a subset of cached options. Call after code imports or bulk mutations. */
export function invalidateFlexOptionsCache(partialKey?: string): void {
  if (!partialKey) {
    flexOptionsCache.clear();
    return;
  }
  for (const key of flexOptionsCache.keys()) {
    if (key.startsWith(partialKey)) {
      flexOptionsCache.delete(key);
    }
  }
}
